// Leads in To book (office-booking prototype's "Unbooked leads"): requests not booked yet, from a
// web form, an email, a call or a referral (leads.list). Book one through Book (jobs.book with the
// lead, which marks it booked), record a new one (leads.create), or close one with a reason
// (leads.close). A refusal says the server's words. Booked and closed leads grow for good, so each list
// is read a page at a time ("Load more"), and the open count is the server's total, not a page's length.

import { useEffect, useRef, useState } from 'react';
import { client } from '../api.ts';
import { errorText } from '../books/ui.tsx';
import { Loading } from '../shared/Loading.tsx';
import { SaveButton, useSaveKey } from '../shared/SaveButton.tsx';
import { phoneText } from '../types.ts';
import {
  closeReason,
  emptyLeadForm,
  LEAD_CLOSE_REASONS,
  LEAD_SOURCES,
  leadInput,
  sourceLabel,
  type Lead,
  type LeadForm,
} from './tobook-model.ts';
import { LEAD_TABS, noLeads, useLeads } from './use-leads.ts';

export function LeadsSection({
  timeZone,
  onBook,
  onChanged,
}: {
  timeZone: string;
  onBook: (lead: Lead) => void;
  /** A lead was added or closed: the Inbox count may change. */
  onChanged: () => void;
}) {
  const { status, setStatus, leads, total, next, moreBusy, error, said, more, added, closed } =
    useLeads(onChanged);
  const [adding, setAdding] = useState(false);
  const [closing, setClosing] = useState<Lead | null>(null);
  const when = (iso: string) =>
    new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    );
  return (
    <section className="sect leads" aria-labelledby="leads-title">
      <div className="row between">
        <h2 id="leads-title">Leads{status === 'open' && leads ? ` (${total})` : ''}</h2>
        <button type="button" className="btn soft" onClick={() => setAdding(true)}>
          New lead
        </button>
      </div>
      <div className="pills" role="group" aria-label="Which leads">
        {LEAD_TABS.map((t) => (
          <button
            key={t.status}
            type="button"
            className="pill"
            aria-pressed={status === t.status}
            onClick={() => setStatus(t.status)}
          >
            {t.label}
          </button>
        ))}
      </div>
      {said && (
        <p className="notice ok" role="status">
          {said}
        </p>
      )}
      {error && <p className="error">{error}</p>}
      {leads === null && !error ? (
        <Loading error={null} what="the leads" />
      ) : leads && leads.length ? (
        <>
          <ul className="rows">
            {leads.map((l) => (
              <li key={l.id} className="stone mat lead">
                <div>
                  <b>{l.name ?? (l.phone ? phoneText(l.phone) : (l.email ?? 'No name'))}</b>
                  <p className="muted small">
                    {sourceLabel(l.source)} · {when(l.createdAt)}
                    {l.name && l.phone ? ` · ${phoneText(l.phone)}` : ''}
                    {l.email && (l.name || l.phone) ? ` · ${l.email}` : ''}
                  </p>
                  <p className="lead-what">{l.summary}</p>
                </div>
                {l.status === 'open' ? (
                  <div className="row">
                    <button type="button" className="btn soft" onClick={() => setClosing(l)}>
                      Close
                    </button>
                    <button
                      type="button"
                      className="btn primary"
                      onClick={() => onBook(l)}
                      aria-label={`Book the lead from ${l.name ?? 'this caller'}`}
                    >
                      Book it
                    </button>
                  </div>
                ) : (
                  <span className={`chip say${l.status === 'booked' ? ' ok' : ''}`}>
                    {l.status === 'booked' ? 'Booked' : 'Closed'}
                  </span>
                )}
              </li>
            ))}
          </ul>
          {next && (
            <button type="button" className="btn soft lead-more" disabled={moreBusy} onClick={more}>
              Load more
            </button>
          )}
        </>
      ) : (
        leads && <p className="muted empty">{noLeads(status)}</p>
      )}
      {adding && (
        <NewLeadDialog
          onClose={() => setAdding(false)}
          onAdded={(l) => {
            setAdding(false);
            added(l);
          }}
        />
      )}
      {closing && (
        <CloseLeadDialog
          lead={closing}
          onClose={() => setClosing(null)}
          onClosed={() => {
            setClosing(null);
            closed(closing);
          }}
        />
      )}
    </section>
  );
}

function NewLeadDialog({ onClose, onAdded }: { onClose: () => void; onAdded: (l: Lead) => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const [f, setF] = useState<LeadForm>(emptyLeadForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = useSaveKey(JSON.stringify(f));
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  const add = async () => {
    const r = leadInput(f);
    if ('error' in r) return setError(r.error);
    setBusy(true);
    setError(null);
    try {
      onAdded(await client.call<Lead>('leads.create', r.input, { idempotencyKey: save.key }));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const set = (patch: Partial<LeadForm>) => setF((x) => ({ ...x, ...patch }));
  return (
    <dialog ref={ref} className="sheet ask" aria-labelledby="lead-new-title" onClose={onClose}>
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy) void add();
        }}
      >
        <h3 id="lead-new-title">New lead</h3>
        <p className="muted">A request that isn’t booked yet. It waits here until it’s booked or closed.</p>
        <label>
          Where it came from
          <select
            className="field"
            value={f.source}
            onChange={(e) => set({ source: e.target.value as LeadForm['source'] })}
          >
            {LEAD_SOURCES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          What they want
          <textarea
            className="field"
            rows={3}
            maxLength={4000}
            value={f.summary}
            onChange={(e) => set({ summary: e.target.value })}
          />
        </label>
        <label>
          Name (optional)
          <input
            className="field"
            maxLength={200}
            value={f.name}
            onChange={(e) => set({ name: e.target.value })}
          />
        </label>
        <label>
          Phone
          <input
            className="field"
            type="tel"
            inputMode="tel"
            value={f.phone}
            onChange={(e) => set({ phone: e.target.value })}
          />
        </label>
        <label>
          Email
          <input
            className="field"
            type="email"
            value={f.email}
            onChange={(e) => set({ email: e.target.value })}
          />
        </label>
        <p className="muted small">A phone or an email, so someone can call them back.</p>
        <div className="row end">
          <button type="button" className="btn soft" onClick={() => ref.current?.close()}>
            Cancel
          </button>
          <SaveButton busy={busy} busyLabel="Saving…" error={error} onClick={() => void add()}>
            Save the lead
          </SaveButton>
        </div>
      </form>
    </dialog>
  );
}

function CloseLeadDialog({
  lead,
  onClose,
  onClosed,
}: {
  lead: Lead;
  onClose: () => void;
  onClosed: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const [choice, setChoice] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reason = closeReason(choice, note);
  const save = useSaveKey(`close:${lead.id}:${reason}`);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  const close = async () => {
    if (!reason) return;
    setBusy(true);
    setError(null);
    try {
      await client.call('leads.close', { id: lead.id, reason }, { idempotencyKey: save.key });
      onClosed();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <dialog ref={ref} className="sheet ask" aria-labelledby="lead-close-title" onClose={onClose}>
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy) void close();
        }}
      >
        <h3 id="lead-close-title">Close this lead with no job?</h3>
        <p className="muted">
          {lead.name ?? 'This lead'}: {lead.summary}
        </p>
        <label>
          Reason (required)
          <select className="field" value={choice} onChange={(e) => setChoice(e.target.value)}>
            <option value="">Something else (write it below)</option>
            {LEAD_CLOSE_REASONS.map((r) => (
              <option key={r}>{r}</option>
            ))}
          </select>
        </label>
        <label>
          {choice ? 'Note (optional)' : 'Why'}
          <textarea
            className="field"
            rows={2}
            maxLength={400}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
        <div className="row end">
          <button type="button" className="btn soft" onClick={() => ref.current?.close()}>
            Keep it open
          </button>
          <SaveButton
            busy={busy}
            busyLabel="Closing…"
            error={error}
            disabled={!reason}
            onClick={() => void close()}
          >
            Close the lead
          </SaveButton>
        </div>
      </form>
    </dialog>
  );
}
